import { type FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useRequestsContext } from "@/contexts/RequestsContext";
import { NotificationType, type OpenShiftRequest, type ReplaceShiftRequest } from "@/utils/models";
import { GAPS_DAY_LABEL_FORMAT, joinLabelParts } from "../gapsLabels";
import { staffingDayKey, staffingDayOfRequest } from "../gapsRequests";
import { useGapsRequestLabels } from "../openShifts/useGapsRequestLabels";
import { closeGapsSheetIfCurrent, type GapsSheetIntentOf, GapsSheetKind } from "../useGapsSheet";
import { useGapsVisibleWeek } from "../useGapsVisibleWeek";
import { useOpenOnGapsDay } from "../useOpenOnGapsDay";
import { GapsPostedActionsStep } from "./GapsPostedActionsStep";
import { GapsPostedHoursStep } from "./GapsPostedHoursStep";
import { GapsPostedIncentiveStep } from "./GapsPostedIncentiveStep";
import { GapsPostedNeededStep } from "./GapsPostedNeededStep";
import { GapsSheetFrame } from "./GapsSheetFrame";
import { GapsPostedStep } from "./gapsPostedSteps";
import { useOpeningStep } from "./useOpeningStep";
import { useSheetOpening } from "./useSheetOpening";

type Props = {
	/** The store's own intent object, or `null` when the sheet is not up (see `useSheetOpening`). */
	target: GapsSheetIntentOf<GapsSheetKind.PostedActions> | null;
	/** Where backing out of "Add someone directly" returns to (the add sheet's `backPath`): Open Shifts' page, `null` on the home. */
	addDirectlyBackPath: string | null;
};

/** The sheet root's test id per step: each is its own sheet to a test, while one drawer stays open. */
const SHEET_TEST_ID: Record<GapsPostedStep, string> = {
	[GapsPostedStep.Actions]: "gaps-posted-sheet",
	[GapsPostedStep.Incentive]: "gaps-posted-incentive-sheet",
	[GapsPostedStep.Hours]: "gaps-posted-hours-sheet",
	[GapsPostedStep.Needed]: "gaps-posted-needed-sheet",
};

/**
 * A posted shift's sheet (P3-10), opened from the home's Posted tab and from Open Shifts: its actions, and the steps they
 * lead to, in ONE drawer that turns its page in place (`SheetStep`), as every gaps sheet does.
 *
 * The request is looked up by id in the live requests on every render, so each step acts on the post as it now stands
 * (a count saved, an incentive changed). The last one found is kept only for the exit animation; a request that leaves
 * the list while the sheet is up — deleted here or elsewhere, filled, merged into another — closes it.
 */
export const GapsPostedSheet: FC<Props> = ({ target, addDirectlyBackPath }) => {
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { staffRequests } = useRequestsContext();
	const { headingOf } = useGapsRequestLabels();
	const { openOnDay } = useOpenOnGapsDay();
	const { isShownDay } = useGapsVisibleWeek();
	const { shown, openingKey } = useSheetOpening(target);
	// Closes THIS opening only: a write that finishes after the user moved on must not close what they moved to.
	const onClose = useCallback((): void => {
		if (shown !== null) closeGapsSheetIfCurrent(shown);
	}, [shown]);

	const live = useMemo(() => (shown === null ? undefined : staffRequests.find((request) => request.id === shown.requestId)), [shown, staffRequests]);
	const [retained, setRetained] = useState<OpenShiftRequest | ReplaceShiftRequest | undefined>(live);
	if (live !== undefined && live !== retained) setRetained(live);
	// DEFAULT: the last request found, for the exit animation only — a request that leaves the list closes the sheet.
	const request = live ?? retained;

	// Every opening starts on the actions; a close leaves the step alone so the drawer animates out on what was showing.
	// A step that finishes a save after the user moved to another opening does not turn that opening's page.
	const { step, setStepFor } = useOpeningStep<GapsPostedStep>(openingKey, GapsPostedStep.Actions);

	useEffect(() => {
		if (target !== null && live === undefined) onClose();
	}, [target, live, onClose]);

	const departmentId = chosenDepartment.department_id;

	if (request === undefined) return null;

	const goTo = (next: GapsPostedStep): void => {
		if (!setStepFor(openingKey, next)) return;
		track("gaps_posted_step_opened", { department_id: departmentId, request_id: request.id, step: next });
	};

	const rule = { dayStart: chosenDepartment.dayStart, scheduled_shift_delta_minutes: chosenDepartment.scheduled_shift_delta_minutes };
	const day = staffingDayOfRequest(request, rule);
	const dayLabel = day.toFormat(GAPS_DAY_LABEL_FORMAT);
	const context = joinLabelParts([headingOf(request), dayLabel]);

	// The home's own test for a day it can show (`MobileGapsShell` clamps any other to the nearest one it can), on the
	// request's STAFFING day — the day the add sheet is opened for, which a shift after midnight rolls back from.
	const isRequestDayLoaded = isShownDay(day);

	// "Add someone directly": the add sheet on the request's day, LINKED to it — the add fills one of its openings.
	const handleAddDirectly = (): void => {
		if (!isRequestDayLoaded) return;
		track("gaps_posted_add_directly_clicked", { department_id: departmentId, request_id: request.id });
		openOnDay({
			kind: GapsSheetKind.AddStaff,
			dayISO: staffingDayKey(day),
			scope: null,
			linkedRequestId: request.id,
			groupKey: null,
			backPath: addDirectlyBackPath,
		});
	};

	const openShift = request.request_type === NotificationType.OpenShift ? (request as OpenShiftRequest) : null;

	const renderStep = () => {
		// Only an open shift has an incentive, hours or a count to edit; a replacement's sheet stays on its actions.
		if (openShift === null || step === GapsPostedStep.Actions) {
			return (
				<GapsPostedActionsStep
					request={request}
					dayLabel={dayLabel}
					onStep={goTo}
					isRequestDayLoaded={isRequestDayLoaded}
					onAddDirectly={handleAddDirectly}
					onClose={onClose}
				/>
			);
		}
		switch (step) {
			case GapsPostedStep.Incentive:
				return <GapsPostedIncentiveStep request={openShift} context={context} onStep={goTo} />;
			case GapsPostedStep.Hours:
				return <GapsPostedHoursStep request={openShift} context={context} onStep={goTo} />;
			case GapsPostedStep.Needed:
				return <GapsPostedNeededStep request={openShift} context={context} onStep={goTo} />;
		}
	};

	return (
		<GapsSheetFrame open={target !== null && live !== undefined} onClose={onClose} dataTestId={SHEET_TEST_ID[step]}>
			{/* Keyed by the opening, so a new one never inherits a half-typed amount or hours from the last. */}
			<Fragment key={openingKey}>{renderStep()}</Fragment>
		</GapsSheetFrame>
	);
};
